import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  Group,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  approvePortalReport,
  getPortalReport,
  withdrawPortalReport,
  type PortalReport,
} from "@/api/portal";
import { AppLayout } from "@/components/AppLayout";
import { config } from "@/config";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t, type MessageKey } from "@/i18n";
import { messages } from "@/messages.en";
import { LabAttachments } from "@/pages/portal/LabAttachments";
import { PinReissueAction } from "@/pages/portal/PinReissueAction";
import { ReviewStateBadge, SeverityBadge } from "@/pages/portal/PortalReportsPage";
import {
  formatBirthDate,
  formatRange,
  formatWhen,
  readHeldBack,
  readReview,
  readSpan,
  relatedInReport,
  reportActErrorKind,
  reportErrorKind,
  roundLikeLab,
  sexLabel,
  type ReviewDisclosure,
  type ReviewGroup,
  type ReviewPassthrough,
  type ReviewResult,
  type ReviewSource,
  type ReviewSpan,
} from "@/pages/portal/reportReview";

/**
 * Lab portal: a read-only review of one stored report (Gate B M1 S7).
 *
 * What the report carries, laid out for the lab's doctor to check before M2 lets them
 * approve it: results with band and severity, Critical first, then every insight grouped
 * by type, Critical first inside a group. Not a second patient renderer: no AI narrative,
 * no patient styling, and the text is shown as plain text exactly as stored.
 *
 * Approve and send, and withdraw (Gate B M2 S11), shown only when onboarding-api has
 * those routes (`config.portalReportApprovalEnabled`) and only on a report that can still
 * move: `pending_review`, or `approved` left unreleased by an earlier attempt. Each asks
 * for a confirmation first. The server decides who may act and records each act as
 * `report.approved` or `report.withdrawn`. Nothing is sent from here: the patient email is
 * M3. The server's own error text is never shown, only a fixed sentence per outcome.
 *
 * The insights are the ones the patient is sent: onboarding-api filters them to the lab's
 * content span (docs#199 audit item 11, plan D9) before they reach this page, and the page
 * names the span and how many insights it keeps back. A BFF that predates the span sends
 * the payload unfiltered, and the page then says that every insight is shown.
 *
 * Whose report it is leads the page (docs#15 note 16595 item 1): the patient's name, date
 * of birth and sex, the lab's patient number and order number, read by the orchestrator
 * from the report's FHIR Patient with the release path's own rules. The FHIR patient id
 * and IAM sub are still never rendered, since neither helps a doctor recognise a patient.
 *
 * The insights the lab's span keeps from the patient follow the report, collapsed, under a
 * heading that says they are not sent (Baher, item 9). They are read-only and never part of
 * the report the doctor approves.
 *
 * Opening this page is recorded server-side as one `report.viewed` audit event. Another
 * lab's report is a 404, the same answer as one that does not exist, and the page says
 * "not found" for both. The server's own error text is never shown.
 */

function insightTypeLabel(type: string): string {
  const key = `portal.insight_type_${type}`;
  // A type this page has no label for is shown as its own code, never dropped.
  return key in messages ? t(key as MessageKey) : type;
}

function DimmedText({ text }: { text: string | null }): JSX.Element | null {
  if (text === null) return null;
  return (
    <Text mt={4} size="xs" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
      {text}
    </Text>
  );
}

/**
 * The sources, each a link that opens in a new tab when it has an `http(s)` address, named
 * by its title (docs#15 note 16595 item 7). A source with no usable address stays text.
 */
function Sources({ sources }: { sources: ReviewSource[] }): JSX.Element | null {
  if (sources.length === 0) return null;
  return (
    <Stack gap={0} mt={4} data-testid="review-sources">
      <Text size="xs" c="dimmed">
        {t("portal.report_review_sources")}:
      </Text>
      {sources.map((source, index) => (
        <Text key={index} size="xs" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
          {source.url !== null ? (
            <Anchor
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              size="xs"
              data-testid="review-source-link"
            >
              {source.title}
            </Anchor>
          ) : (
            source.title
          )}
          {source.meta !== null ? ` (${source.meta})` : null}
        </Text>
      ))}
    </Stack>
  );
}

export function PortalReportReviewPage(): JSX.Element {
  const { reportId = "" } = useParams<{ reportId: string }>();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["portal-report", reportId],
    queryFn: () => getPortalReport(reportId),
    retry: false,
    // Every fetch is one audited `report.viewed` event, so each open of this page fetches
    // exactly once: nothing is served from cache on a re-open (that open would go
    // unaudited), nothing refetches on focus, reconnect or a timer, and the payload is not
    // kept in memory once the doctor leaves the page.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const crumbs = [
    { label: t("portal.reports"), to: "/portal/reports" },
    { label: t("portal.report_review") },
  ];

  if (isPending) {
    return (
      <AppLayout breadcrumbs={crumbs}>
        <Title order={1}>{t("portal.report_review")}</Title>
        <TableSkeleton label={t("portal.report_review_loading")} />
      </AppLayout>
    );
  }

  if (isError) {
    const kind = reportErrorKind(error);
    return (
      <AppLayout breadcrumbs={crumbs}>
        <Title order={1}>{t("portal.report_review")}</Title>
        <Alert color="red" mt="md" title={t("portal.report_review")}>
          {kind === "not_found"
            ? t("portal.report_review_not_found")
            : kind === "forbidden"
              ? t("portal.reports_forbidden")
              : t("portal.report_review_unavailable")}
        </Alert>
      </AppLayout>
    );
  }

  const review = readReview(data.report_payload);
  const span = readSpan(data);
  const heldBack = readHeldBack(data);
  const notices = review.disclosures.filter((d) => !d.withholds);
  const missing = review.disclosures.filter((d) => d.withholds);

  return (
    <AppLayout breadcrumbs={crumbs}>
      <Title order={1}>{t("portal.report_review")}</Title>
      <ReviewHeaderAndActions report={data} />
      <SpanNote span={span} />
      {review.skipped > 0 ? (
        <Alert color="orange" mt="md" data-testid="review-skipped">
          {t("portal.report_review_skipped")}
        </Alert>
      ) : null}

      <Disclosures
        disclosures={missing}
        title={t("portal.report_review_disclosures")}
        color="yellow"
        testId="review-disclosures"
      />
      <Disclosures
        disclosures={notices}
        title={t("portal.report_review_notices")}
        color="blue"
        testId="review-notices"
      />

      <LabAttachments reportId={data.report_id} />

      <Title order={2} mt="xl" mb="sm">
        {t("portal.report_review_results")}
      </Title>
      {review.results.length === 0 ? (
        <Text c="dimmed">{t("portal.report_review_results_empty")}</Text>
      ) : (
        <ResultsTable results={review.results} />
      )}

      {review.passthrough.length > 0 ? (
        <>
          <Title order={3} mt="lg" mb="xs">
            {t("portal.report_review_passthrough")}
          </Title>
          <Text size="sm" c="dimmed" mb="xs">
            {t("portal.report_review_passthrough_note")}
          </Text>
          <PassthroughTable rows={review.passthrough} />
        </>
      ) : null}

      <Title order={2} mt="xl" mb="sm">
        {t("portal.report_review_insights")}
      </Title>
      {review.groups.length === 0 ? (
        <Text c="dimmed">{t("portal.report_review_insights_empty")}</Text>
      ) : (
        <Stack gap="lg">
          {review.groups.map((group) => (
            <InsightGroup key={group.type} group={group} results={review.results} />
          ))}
        </Stack>
      )}

      {heldBack.length > 0 ? <HeldBack groups={heldBack} results={review.results} /> : null}
    </AppLayout>
  );
}

/**
 * The content notices, under one title. Notices that withhold nothing (the pregnancy note)
 * sit under a neutral title, so a doctor does not read them as content missing (docs#15
 * note 16595 item 13). The notice text is shown exactly as written.
 */
function Disclosures({
  disclosures,
  title,
  color,
  testId,
}: {
  disclosures: ReviewDisclosure[];
  title: string;
  color: string;
  testId: string;
}): JSX.Element | null {
  if (disclosures.length === 0) return null;
  return (
    <Alert color={color} mt="md" title={title} data-testid={testId}>
      <Stack gap={4}>
        {disclosures.map((disclosure, index) => (
          <Text key={index} size="sm">
            {disclosure.note}
          </Text>
        ))}
      </Stack>
    </Alert>
  );
}

/**
 * The insights the lab's content span keeps from the patient, read-only and collapsed, after
 * the report and apart from it (Baher, docs#15 note 16595 item 9).
 */
function HeldBack({
  groups,
  results,
}: {
  groups: ReviewGroup[];
  results: ReviewResult[];
}): JSX.Element {
  const count = groups.reduce((sum, group) => sum + group.insights.length, 0);
  return (
    <Accordion variant="contained" mt="xl" data-testid="review-held-back">
      <Accordion.Item value="held-back">
        <Accordion.Control>
          <Text fw={600}>{t("portal.report_review_held_back_title")}</Text>
          <Text size="sm" c="dimmed">
            {count}{" "}
            {count === 1
              ? t("portal.report_review_held_back_count_one")
              : t("portal.report_review_held_back_count_many")}
          </Text>
        </Accordion.Control>
        <Accordion.Panel>
          <Text size="sm" c="dimmed" mb="sm">
            {t("portal.report_review_held_back_note")}
          </Text>
          <Stack gap="lg">
            {groups.map((group) => (
              <InsightGroup key={group.type} group={group} results={results} />
            ))}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

/** Which content the page shows: the lab's span, or, from an older BFF, everything. */
function SpanNote({ span }: { span: ReviewSpan | null }): JSX.Element {
  if (span === null) {
    return (
      <Alert color="blue" mt="md" data-testid="review-span-note">
        {t("portal.report_review_note")}
      </Alert>
    );
  }
  const outsideKey =
    span.outside === 1
      ? "portal.report_review_outside_span_one"
      : "portal.report_review_outside_span_many";
  return (
    <Alert color="blue" mt="md" data-testid="review-span-note">
      <Text size="sm">
        {t("portal.report_review_note_span")} <Code data-testid="review-span">{span.span}</Code>
      </Text>
      {span.outside > 0 ? (
        <Text size="sm" mt={4} data-testid="review-span-outside">
          {span.outside} {t(outsideKey)}
        </Text>
      ) : null}
      {span.source === "fail_closed" || span.source === "flag_off" ? (
        <Text size="sm" mt={4} data-testid="review-span-source">
          {span.source === "fail_closed"
            ? t("portal.report_review_span_fail_closed")
            : t("portal.report_review_span_flag_off")}
        </Text>
      ) : null}
    </Alert>
  );
}

type Act = "approve" | "withdraw";

/** The report's identity and state, and the doctor's two acts on it (Gate B M2 S11). */
function ReviewHeaderAndActions({ report }: { report: PortalReport }): JSX.Element {
  // The report as the last successful act returned it. The page's query is never refetched
  // (each fetch is an audited view), so the new state is shown from the act's response.
  const [current, setCurrent] = useState<PortalReport>(report);
  const [confirming, setConfirming] = useState<Act | null>(null);
  const [done, setDone] = useState<Act | null>(null);

  const act = useMutation({
    mutationFn: (which: Act) =>
      which === "approve"
        ? approvePortalReport(current.report_id)
        : withdrawPortalReport(current.report_id),
    onSuccess: (updated, which) => {
      // An act's answer carries no identity (the BFF reads it on the view only), so the
      // patient and the lab shown before the act are kept.
      setCurrent({
        ...updated,
        patient: updated.patient ?? current.patient ?? null,
        lab_order_number: updated.lab_order_number ?? current.lab_order_number ?? null,
        lab_report_id: updated.lab_report_id ?? current.lab_report_id ?? null,
        organization_name: updated.organization_name ?? current.organization_name ?? null,
        organization_timezone:
          updated.organization_timezone ?? current.organization_timezone ?? null,
      });
      setDone(which);
      setConfirming(null);
    },
    onError: () => setConfirming(null),
  });

  const movable = current.review_state === "pending_review" || current.review_state === "approved";
  const showActions = config.portalReportApprovalEnabled && movable;
  const errorKind = act.isError ? reportActErrorKind(act.error) : null;

  return (
    <>
      <PatientCard report={current} />
      <Group mt="xs" gap="lg">
        <ReviewStateBadge state={current.review_state} />
        <Text size="sm" c="dimmed">
          {t("portal.report_review_generated")}:{" "}
          {formatWhen(current.generated_at, current.organization_timezone)}
        </Text>
        {config.brandingEnabled ? (
          // Plan B7: the patient's view in the lab's saved branding, before approving.
          <Button
            component={Link}
            to={`/portal/reports/${encodeURIComponent(current.report_id)}/branded-preview`}
            variant="subtle"
            size="xs"
            data-testid="review-branded-preview"
          >
            {t("portal.report_branded_preview")}
          </Button>
        ) : null}
      </Group>
      {showActions && confirming === null ? (
        <SimpleGrid cols={{ base: 1, sm: 2 }} mt="md" data-testid="review-actions">
          <Stack gap={4}>
            <Button onClick={() => setConfirming("approve")} disabled={act.isPending}>
              {t("portal.report_approve")}
            </Button>
            <Text size="xs" c="dimmed" data-testid="review-approve-explained">
              {t("portal.report_approve_explained")}
            </Text>
          </Stack>
          <Stack gap={4}>
            <Button
              variant="outline"
              color="red"
              onClick={() => setConfirming("withdraw")}
              disabled={act.isPending}
            >
              {t("portal.report_withdraw")}
            </Button>
            <Text size="xs" c="dimmed" data-testid="review-withdraw-explained">
              {t("portal.report_withdraw_explained")}
            </Text>
          </Stack>
        </SimpleGrid>
      ) : null}
      {showActions && confirming !== null ? (
        <Alert
          color={confirming === "approve" ? "blue" : "red"}
          mt="md"
          data-testid="review-confirm"
        >
          <Text size="sm">
            {confirming === "approve"
              ? t("portal.report_approve_confirm")
              : t("portal.report_withdraw_confirm")}
          </Text>
          <Group mt="sm">
            <Button
              color={confirming === "approve" ? "blue" : "red"}
              loading={act.isPending}
              onClick={() => act.mutate(confirming)}
            >
              {t("portal.report_confirm")}
            </Button>
            <Button variant="default" disabled={act.isPending} onClick={() => setConfirming(null)}>
              {t("portal.report_cancel")}
            </Button>
          </Group>
        </Alert>
      ) : null}
      <PinReissueAction report={current} />
      {done !== null ? (
        <Alert color="green" mt="md" data-testid="review-act-done">
          {done === "approve"
            ? t("portal.report_approved_done")
            : t("portal.report_withdrawn_done")}
        </Alert>
      ) : null}
      {errorKind !== null ? (
        <Alert color="red" mt="md" data-testid="review-act-error">
          {errorKind === "conflict" || errorKind === "not_found"
            ? t("portal.report_act_conflict")
            : errorKind === "forbidden"
              ? t("portal.report_act_forbidden")
              : t("portal.report_act_unavailable")}
        </Alert>
      ) : null}
    </>
  );
}

/**
 * Whose report this is, at the top of the page (docs#15 note 16595 item 1). A field the
 * orchestrator could not read says so rather than leaving a gap a doctor could miss.
 */
function PatientCard({ report }: { report: PortalReport }): JSX.Element {
  const patient = report.patient ?? null;
  const missing = t("portal.identity_not_available");
  const lab = report.organization_name ?? report.organization_id;
  const rows: [string, string | null, string][] = [
    [t("portal.identity_name"), patient?.display_name ?? null, "identity-name"],
    [t("portal.identity_birth_date"), formatBirthDate(patient?.birth_date), "identity-birth-date"],
    [t("portal.identity_sex"), sexLabel(patient?.sex), "identity-sex"],
    [t("portal.identity_order_number"), report.lab_order_number ?? null, "identity-order"],
  ];
  // docs#239 item 26: the id the lab's own system was told, beside its order number, so the
  // doctor can match this report to the lab's message. Our store id stays below, dimmed.
  // With lab report events off the lab was told the store id, so there is no second id.
  if (report.lab_report_id) {
    rows.push([t("portal.identity_lab_report_id"), report.lab_report_id, "identity-lab-report-id"]);
  }
  if (patient?.lab_patient_number) {
    rows.push([t("portal.identity_patient_number"), patient.lab_patient_number, "identity-mrn"]);
  }
  return (
    <Card withBorder mt="xs" padding="sm" data-testid="review-patient">
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="xs">
        {rows.map(([label, value, testId]) => (
          <div key={testId}>
            <Text size="xs" c="dimmed">
              {label}
            </Text>
            <Text fw={600} c={value === null ? "dimmed" : "inherit"} data-testid={testId}>
              {value ?? missing}
            </Text>
          </div>
        ))}
        <div>
          <Text size="xs" c="dimmed">
            {t("portal.organization")}
          </Text>
          <Text data-testid="identity-lab">{lab}</Text>
        </div>
      </SimpleGrid>
      <Text size="xs" c="dimmed" mt="xs" data-testid="identity-store-id">
        {t("portal.identity_report_id")}: <Code>{report.report_id}</Code>
      </Text>
    </Card>
  );
}

/** A number or range followed by its unit, when there is one. */
function withUnit(text: string, unit: string | null): string {
  return unit ? `${text} ${unit}` : text;
}

function ResultsTable({ results }: { results: ReviewResult[] }): JSX.Element {
  return (
    <Table withTableBorder data-testid="review-results">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("portal.report_review_col_result")}</Table.Th>
          <Table.Th>{t("portal.report_review_col_value")}</Table.Th>
          <Table.Th>{t("portal.report_review_col_band")}</Table.Th>
          <Table.Th>{t("portal.report_review_col_severity")}</Table.Th>
          <Table.Th>{t("portal.report_review_col_range")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {results.map((row, index) => (
          <Table.Tr key={`${row.biomarker}-${index}`} data-testid="review-result-row">
            <Table.Td>
              <Text fw={600}>{row.displayName ?? row.biomarker}</Text>
              <ResultNames row={row} />
              <DimmedText text={row.whatWhy} />
              <Sources sources={row.citations} />
            </Table.Td>
            <Table.Td data-testid="review-result-value">
              {row.labMode ? <LabValue row={row} /> : <BandedValue row={row} />}
            </Table.Td>
            <Table.Td>
              <Group gap={6}>
                <Text>{row.band}</Text>
                {row.inconclusive ? (
                  <Badge color="gray" variant="outline">
                    {t("portal.report_review_inconclusive")}
                  </Badge>
                ) : null}
              </Group>
            </Table.Td>
            <Table.Td>
              <SeverityBadge severity={row.severity} />
            </Table.Td>
            <Table.Td data-testid="review-result-range">
              <ResultRange row={row} />
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

/**
 * The symbol and the lab's own name under the display name, each shown once: the symbol
 * only when a display name leads, the lab's name only when it says something new.
 */
function ResultNames({ row }: { row: ReviewResult }): JSX.Element | null {
  const names = [
    row.displayName !== null ? row.biomarker : null,
    row.sentName !== null && row.sentName !== row.displayName && row.sentName !== row.biomarker
      ? row.sentName
      : null,
  ].filter((name): name is string => name !== null);
  if (names.length === 0) return null;
  return (
    <Text size="xs" c="dimmed" data-testid="review-result-names">
      {names.join(" · ")}
    </Text>
  );
}

/**
 * A lab report's value (docs#199): the lab's own number and unit first, as its printed
 * report and the patient page show it. The number we banded follows only when we converted,
 * so the doctor can see both.
 *
 * "We converted" is `sent_unit`, which the orchestrator sets only when it converted away
 * from the lab's unit. Never a comparison of our band's unit with the lab's: the two differ
 * in spelling alone (`mmol/l` from the lab, `mmol/L` on the band) with nothing converted,
 * and the line would then claim a conversion that never happened.
 */
function LabValue({ row }: { row: ReviewResult }): JSX.Element {
  const converted = row.value !== null && row.sentUnit !== null;
  return (
    <>
      {row.labValue === null ? "—" : withUnit(row.labValue, row.labUnit)}
      {converted ? (
        <Text
          size="xs"
          c="dimmed"
          data-testid="review-result-banded"
          title={row.value !== null ? String(row.value) : undefined}
        >
          {t("portal.report_review_banded_as")}{" "}
          {row.value !== null ? withUnit(roundLikeLab(row.value, row.labValue), row.unit) : null}
        </Text>
      ) : null}
    </>
  );
}

/** A result from before docs#199: the number we banded, and the lab's unit if we converted. */
function BandedValue({ row }: { row: ReviewResult }): JSX.Element {
  return (
    <>
      {row.value === null ? "—" : withUnit(String(row.value), row.unit)}
      {row.sentUnit !== null && row.sentUnit !== row.unit ? (
        <Text size="xs" c="dimmed" data-testid="review-result-sent-unit">
          {t("portal.report_review_lab_sent_unit")} {row.sentUnit}
        </Text>
      ) : null}
    </>
  );
}

/**
 * The lab's printed range on a lab report. When the lab printed none, our own normal band,
 * labelled as ours so it is never read as the lab's. A result from before docs#199 shows our
 * normal band as it always did.
 */
function ResultRange({ row }: { row: ReviewResult }): JSX.Element {
  const ours =
    row.normalLow === null && row.normalHigh === null
      ? formatRange(null, null)
      : withUnit(formatRange(row.normalLow, row.normalHigh), row.unit);
  if (!row.labMode) return <>{ours}</>;
  if (row.labRangeLow !== null || row.labRangeHigh !== null) {
    return (
      <>
        {withUnit(formatRange(row.labRangeLow, row.labRangeHigh), row.labRangeUnit ?? row.labUnit)}
      </>
    );
  }
  if (row.normalLow === null && row.normalHigh === null) return <>{ours}</>;
  return (
    <>
      {ours}
      <Text size="xs" c="dimmed" data-testid="review-result-sr-range">
        {t("portal.report_review_sr_range")}
      </Text>
    </>
  );
}

function PassthroughTable({ rows }: { rows: ReviewPassthrough[] }): JSX.Element {
  return (
    <Table withTableBorder data-testid="review-passthrough">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("portal.report_review_col_result")}</Table.Th>
          <Table.Th>{t("portal.report_review_col_value")}</Table.Th>
          <Table.Th>{t("portal.report_review_col_lab_range")}</Table.Th>
          <Table.Th>{t("portal.report_review_col_reason")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row, index) => (
          <Table.Tr key={`${row.name}-${index}`}>
            <Table.Td>
              <Text>{row.name}</Text>
              <DimmedText text={row.note} />
              <DimmedText text={row.whatWhy} />
            </Table.Td>
            <Table.Td>{`${row.value}${row.unit ? ` ${row.unit}` : ""}`}</Table.Td>
            <Table.Td>{formatRange(row.rangeLow, row.rangeHigh)}</Table.Td>
            <Table.Td>
              <Code>{row.reason}</Code>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function InsightGroup({
  group,
  results,
}: {
  group: ReviewGroup;
  results: ReviewResult[];
}): JSX.Element {
  return (
    <section data-testid="review-insight-group" data-insight-type={group.type}>
      <Title order={3} mb="xs">
        {insightTypeLabel(group.type)}
      </Title>
      <Stack gap="sm">
        {group.insights.map((insight) => {
          const related = relatedInReport(insight.relatedBiomarkers, results);
          return (
            <Card key={insight.id} withBorder padding="sm" data-testid="review-insight">
              <Group justify="space-between" align="flex-start">
                <Text fw={600}>{insight.title ?? insightTypeLabel(insight.type)}</Text>
                {/* "None" on an insight read as "no tips" (docs#15 note 16595 item 12). A
                    result keeps it, where it is a judgement in a Severity column. */}
                {insight.severity !== null && insight.severity.toLowerCase() !== "none" ? (
                  <SeverityBadge severity={insight.severity} />
                ) : null}
              </Group>
              <Text mt="xs" style={{ whiteSpace: "pre-wrap" }}>
                {insight.body}
              </Text>
              {insight.disclaimer !== null ? (
                <Text mt="xs" size="sm" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
                  {insight.disclaimer}
                </Text>
              ) : null}
              {related.length > 0 ? (
                <Text mt="xs" size="xs" c="dimmed" data-testid="review-insight-related">
                  {t("portal.report_review_related")}: {related.join(", ")}
                </Text>
              ) : null}
              <Sources sources={insight.citations} />
            </Card>
          );
        })}
      </Stack>
    </section>
  );
}
